/**
 * `GET /api/v3/org/context` — the one call that decides whether the B2B surfaces
 * are drawn at all (integration guide §2).
 *
 * THE ONE RULE (§0): an organization prepays, its people use the same app as
 * everybody else. A non-member gets `member: null` and every screen renders
 * exactly as it did before B2B existed — that is structural, not a flag, so
 * nothing in this file is ever consulted behind an "is this a B2B build" branch.
 *
 * Role is resolved PER ENDPOINT, never globally: `/context`, `/wheel`,
 * `/engagement`, `/programs` and `/counseling/*` look for the caller's `member`
 * row, while `/children`, `/consents` and `/counseling/guardian-requests` look
 * for their `guardian` row. A parent who is a guardian at their child's school
 * and nothing else therefore gets `member: null` here AND a populated children
 * list — which is why {@link OrgGuardianFacade} never gates on this model.
 */

/** Drives the product wording, and gates the schools-only surfaces (§2). */
export type OrgKind = 'university' | 'school' | 'company';

/** Every role the API can put on a membership row. `/context` only ever returns `member`. */
export type OrgRole = 'member' | 'org_admin' | 'counselor' | 'teacher' | 'guardian';

/** Schools only; null for universities and companies (§2). */
export type OrgStage = 'kindergarten' | 'primary' | 'middle' | 'high';

export interface Organization {
  id: number;
  kind: OrgKind;
  /** Arabic name (English only when `Accept-Language: en` AND `name_en` is set). */
  name: string;
  /**
   * The RAW storage path the API sent (`organizations/qu-logo.png`) — kept so a
   * consumer can re-prefix it against a different host if it ever needs to.
   * Trap 18: this is not a URL.
   */
  logoPath: string | null;
  /** {@link logoPath} resolved against the API's public disk; null when there is no logo. */
  logoUrl: string | null;
  /** Ready to render («مُهدى من جامعة القصيم»). Never re-translate (§1.5). */
  badge: string;
  /** Often null — the organization has to have set it. */
  privacyLine: string | null;
}

export interface OrgMembership {
  /** Shown to the member; a counselor references it. */
  memberCode: string;
  role: OrgRole;
  /** College / department / class display name. Null when unassigned. */
  unit: string | null;
  stage: OrgStage | null;
}

/**
 * `entitlement.active` is ALWAYS present; when false the block carries no other
 * keys, which is why every other field here is nullable rather than optional.
 * `endsAt: null` with `active: true` is an open-ended gift, not a missing value.
 */
export interface OrgEntitlement {
  active: boolean;
  label: string | null;
  /** ISO 8601, or null for an open-ended gift. */
  endsAt: string | null;
  daysLeft: number | null;
}

/** One segment of the Home switch. The server owns the list AND its order (§2). */
export interface OrgHomeMode {
  key: string;
  label: string;
}

export interface OrgHome {
  /**
   * The PRODUCT's name, not the organization's — university → «الرفاه الطلابي»,
   * company → «فريقك بخير», school → «رفاه طلاب <name>».
   */
  productLine: string;
  /**
   * The segmented switch, in the order given. `start` is always first and can
   * never be switched off; an org that has not bought the wheel simply will not
   * have it here, and that is a settings change on the server, not a release.
   */
  modes: readonly OrgHomeMode[];
  /** Always `"start"` today. Honour it rather than hardcoding. */
  defaultMode: string;
}

/**
 * The switch a MEMBER falls back to when `/context` sends no `home.modes`.
 *
 * This does not contradict "do not compile the three modes in" (§2): an org that
 * has not bought the wheel sends the modes it DID buy, and that list is honoured
 * exactly as sent. This only covers the different case of a member whose payload
 * carries no `home` block at all — a server that has not rolled the field out
 * yet, or a row with the home settings unset. Without it that member gets a hub
 * with no switch and a blank second line in the lockup; with it they get the
 * documented three, which is also what {@link OrganizationsMockRepository}
 * serves offline, so the two paths cannot drift.
 *
 * Arabic, as the server sends it (§1.5) — these labels are contractual copy from
 * the signed decks, not UI strings this app is allowed to author or translate.
 */
export const ORG_HOME_FALLBACK_MODES: readonly OrgHomeMode[] = [
  { key: 'start', label: 'نبدأ سوا' },
  { key: 'wheel', label: 'عجلة الأبعاد' },
  { key: 'engagement', label: 'منظومة الانخراط' },
];

/** `start` is always first and can never be switched off (§2). */
export const ORG_HOME_FALLBACK_DEFAULT_MODE = 'start';

/**
 * The lockup's second line when `home.product_line` is missing.
 *
 * §2's own table gives the mapping: university → «الرفاه الطلابي», company →
 * «فريقك بخير», school → «رفاه طلاب <name>». A school with no name would compose
 * a dangling «رفاه طلاب », so that case takes the neutral line instead.
 */
export function orgProductLine(kind: OrgKind, name: string): string {
  if (kind === 'company') return 'فريقك بخير';
  if (kind === 'school' && name.trim() !== '') return `رفاه طلاب ${name}`;
  return 'الرفاه الطلابي';
}

/** The whole `home` block for a member whose payload carries none. */
export function orgHomeFallback(organization: Organization): OrgHome {
  return {
    productLine: orgProductLine(organization.kind, organization.name),
    modes: ORG_HOME_FALLBACK_MODES,
    defaultMode: ORG_HOME_FALLBACK_DEFAULT_MODE,
  };
}

/** `data.member` when the caller is an active member of a live organization. */
export interface OrgMember {
  organization: Organization;
  membership: OrgMembership;
  entitlement: OrgEntitlement;
  /** Who a case escalates to («وحدة الإرشاد الطلابي» · «ركن الإرشاد الطلابي» · «فريق تلبينة»). */
  escalationTarget: string | null;
  home: OrgHome;
}

/** The whole `/context` answer. `null` IS the ordinary case — never an error (trap 1). */
export type OrgContext = OrgMember | null;

/** Schools-only surfaces (counseling §7, guardian §8) light up for this kind alone. */
export function isSchool(member: OrgContext): boolean {
  return member?.organization.kind === 'school';
}

/**
 * Coverage is on → show the badge and suppress purchase CTAs. Mirrors
 * `org_grant.hide_purchase` on `GET /v3/subscription` (§9.5), which is the
 * account-wide version of the same fact.
 */
export function hasActiveEntitlement(member: OrgContext): boolean {
  return member?.entitlement.active === true;
}
